// ============================================================================
// useBoardViewPrefs — one person's arrangement of one board, read from and
// written to user_board_view_prefs (item 818). RLS gives each person only
// their own row; nothing is kept in the browser, so the arrangement follows
// the person across devices.
//
// Writes are optimistic: the screen rearranges at once and the row is upserted
// behind it. If the save fails the last good row is put back and `error`
// says so — the board never lies about what was saved.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../auth';
import { errorMessage } from '../errors';
import {
  DEFAULT_BOARD_VIEW,
  moveCard,
  placeCard,
  prefsFromRow,
  togglePin,
  type BoardKey,
  type BoardSortMode,
  type BoardViewPrefs,
} from './board-view';

export interface BoardViewApi {
  prefs: BoardViewPrefs;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** True once a person is known, so the controls can render. */
  ready: boolean;
  setSortMode: (mode: BoardSortMode) => void;
  togglePin: (id: string) => void;
  /** Drag: drop `activeId` onto `overId` inside one column shown as `shownIds`. */
  place: (shownIds: readonly string[], activeId: string, overId: string) => void;
  /** Phone: step one card up or down inside its column. */
  move: (shownIds: readonly string[], id: string, direction: 'up' | 'down') => void;
}

export function useBoardViewPrefs(boardKey: BoardKey): BoardViewApi {
  const { profile } = useAuth();
  const userId = profile?.id ?? null;
  const [prefs, setPrefs] = useState<BoardViewPrefs>(DEFAULT_BOARD_VIEW);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The last row the database confirmed, to restore on a failed save.
  const confirmed = useRef<BoardViewPrefs>(DEFAULT_BOARD_VIEW);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      const { data, error: err } = await supabase
        .from('user_board_view_prefs')
        .select('sort_mode, pinned_ids, manual_order')
        .eq('user_id', userId)
        .eq('board_key', boardKey)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError(errorMessage(err, 'Could not load how you arranged this board.'));
        setPrefs(DEFAULT_BOARD_VIEW);
        confirmed.current = DEFAULT_BOARD_VIEW;
      } else {
        const next = prefsFromRow(data);
        setPrefs(next);
        confirmed.current = next;
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, boardKey]);

  const save = useCallback(
    async (next: BoardViewPrefs) => {
      if (!userId) return;
      setPrefs(next);
      setSaving(true);
      setError(null);
      const { error: err } = await supabase.from('user_board_view_prefs').upsert(
        {
          user_id: userId,
          board_key: boardKey,
          sort_mode: next.sortMode,
          pinned_ids: next.pinnedIds,
          manual_order: next.manualOrder,
        },
        { onConflict: 'user_id,board_key' },
      );
      if (err) {
        setError(errorMessage(err, 'Could not save how you arranged this board.'));
        setPrefs(confirmed.current);
      } else {
        confirmed.current = next;
      }
      setSaving(false);
    },
    [userId, boardKey],
  );

  return {
    prefs,
    loading,
    saving,
    error,
    ready: !!userId && !loading,
    setSortMode: (mode) => void save({ ...prefs, sortMode: mode }),
    togglePin: (id) => void save(togglePin(prefs, id)),
    place: (shownIds, activeId, overId) => {
      const next = placeCard(prefs, shownIds, activeId, overId);
      if (next !== prefs) void save(next);
    },
    move: (shownIds, id, direction) => {
      const next = moveCard(prefs, shownIds, id, direction);
      if (next !== prefs) void save(next);
    },
  };
}
